import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { mesServiceOptions } from '../../../mock-data/services-mock-data';

export interface MesFormData {
  selectedService: string | null;
  totalMachines: number;
}

@Injectable({
  providedIn: 'root',
})
export class MesFormService {
  constructor(private fb: FormBuilder) {}

  createMesForm(data?: MesFormData): FormGroup<MesForm> {
    const form = this.fb.group<MesForm>({
      selectedService: this.fb.control(null, [Validators.required]),
      totalMachines: this.fb.control(0, [Validators.required, Validators.min(1)]),
    });

    // Initialize with data if provided
    if (data) {
      this.patchFormWithData(form, data);
    }

    return form;
  }

  private patchFormWithData(form: FormGroup<MesForm>, data: MesFormData): void {
    form.patchValue(data);
  }

  selectService(form: FormGroup<MesForm>, serviceValue: string): void {
    form.get('selectedService')?.setValue(serviceValue);
  }

  getFormData(form: FormGroup<MesForm>): MesFormData {
    const value = form.value;
    return {
      selectedService: value.selectedService ?? null,
      totalMachines: value.totalMachines ?? 0,
    };
  }

  onTotalMachinesChange(form: FormGroup<MesForm>, value: number | null): void {
    const numericValue = value || 0;
    form.get('totalMachines')?.setValue(numericValue);
    form.updateValueAndValidity();
  }

  // Helper methods for dropdown value handling
  getDropdownValue(value: any): string {
    if (typeof value === 'string') {
      return value;
    }
    if (value && typeof value === 'object' && 'value' in value) {
      return value.value;
    }
    return '';
  }

  findDropdownObject(options: any[], value: string): any {
    if (!value) return null;
    return options.find((option) => option.value === value) || null;
  }

  // Static data methods
  getServiceOptions() {
    return mesServiceOptions;
  }

  getServiceDescription(serviceValue: string): string {
    const service = this.getServiceOptions().find((s: any) => s.value === serviceValue);
    return service?.description || '';
  }

  // Debug method
  debugFormState(form: FormGroup<MesForm>): void {
    console.log('=== MES Form Debug ===');
    console.log('Form Valid:', form.valid);
    console.log('Form Invalid:', form.invalid);
    console.log('Form Pending:', form.pending);
    console.log('Form Dirty:', form.dirty);
    console.log('Form Touched:', form.touched);
    console.log('Form Errors:', form.errors);

    console.log('Selected Service:', form.get('selectedService')?.value);
    console.log('Selected Service Valid:', form.get('selectedService')?.valid);
    console.log('Selected Service Errors:', form.get('selectedService')?.errors);

    console.log('Total Machines:', form.get('totalMachines')?.value);
    console.log('Total Machines Valid:', form.get('totalMachines')?.valid);
    console.log('Total Machines Errors:', form.get('totalMachines')?.errors);

    // Check all form controls
    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);
      console.log(`${key}:`, {
        value: control?.value,
        valid: control?.valid,
        invalid: control?.invalid,
        errors: control?.errors,
        touched: control?.touched,
        dirty: control?.dirty,
      });
    });
  }
}

export interface MesForm {
  selectedService: FormControl<string | null>;
  totalMachines: FormControl<number | null>;
}
